/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { gqlType } from "@hifieng/common";
import { NavLink, useHistory, useRouteMatch } from "react-router-dom";
import { useOrganizationContext } from "../../../contexts/OrganizationProvider";
import { CSVLink } from "react-csv";

import styles from "./index.module.scss";
import { Table, Row, Head, Cell } from "../../Table";
import { TextLink, Primary } from "../../Button";
import { ChevronRight, Download } from "../../Icons";
import Completed from "../PigRun/Completed";
import EmptyListState from "../../EmptyListState";
import PaginationButtons from "../../PaginationButtons";

import formatCompletedRuns from "./formatCompletedRuns";
import { useCompletedPigs } from "./useCompletedPigs";
import DesktopFilters from "./DesktopFilters";
import MobileFilters from "./MobileFilters";

import { usePigFiltersContext } from "../PigFiltersProvider";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { pigsLimit } from "../../../helpers/pagination";
import { useMemoizedTimes } from "../../../hooks/useMemorizedTimes";
import LoadingWrapper from "../../LoadingSpinnerWrapper";
import { ITabContent } from "../../TabbedContent";
import { OptionType } from "../../../types/GeneralTypes";
import { formatPigTypeForDisplay } from "../../../helpers/pigMetadata";

interface ICompletedPigsTabContent extends ITabContent {
  loading: boolean;
  empty?: boolean;
}

const TABLE_HEADERS = [
  "name",
  "pig type",
  "start",
  "end",
  "updates",
  "critical events"
];

// Separator used to pack multiple raw pig type values into a single filter
// option value. Must not be "," because the filters provider joins/splits the
// selected option values with commas when persisting them to the URL.
const PIG_TYPE_VALUE_SEPARATOR = "|";

const CompletedRuns = ({ empty, onActivated }: ICompletedPigsTabContent) => {
  const { activeOrg } = useOrganizationContext();
  const { setQueryParams, queryParams } = useQueryParams();

  const history = useHistory();
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    sortBy,
    selectedPigTypes,
    selectedUpdatesTypes,
    resetAllFilters,
    page,
    selectedPipelines
  } = usePigFiltersContext();
  const updatesType = selectedUpdatesTypes.length
    ? (selectedUpdatesTypes as Array<gqlType.UpdatesType>)
    : undefined;
  // A single option can represent multiple raw pig types that share a display
  // label (its value is a "|"-joined list). Expand those back into the full set
  // of raw pig types so the query filters on every underlying value.
  // Note: "|" is used rather than "," because the filters provider stores the
  // selected option values as a comma-separated string in the URL.
  const expandedPigTypes = selectedPigTypes.reduce<Array<string>>(
    (acc, value) => acc.concat(value.split(PIG_TYPE_VALUE_SEPARATOR)),
    []
  );
  const types = expandedPigTypes.length
    ? (expandedPigTypes as Array<gqlType.PigType>)
    : undefined;
  const { start, end } = queryParams;

  const timeRange = useMemoizedTimes(
    start,
    end,
    activeOrg && activeOrg.utcOffset
  );

  const csvExportLimit = parseInt(process.env.CSV_EXPORT_LIMIT || "0", 0);

  const filters = {
    updatesType,
    types,
    timeRange,
    sortBy: (sortBy as gqlType.SortByTypes) || gqlType.SortByTypes.TimeDesc,
    limit: exporting ? csvExportLimit : pigsLimit,
    offset: exporting ? 0 : (page - 1) * pigsLimit,
    pipelines: selectedPipelines.length ? selectedPipelines : undefined
  };
  const { pigRuns, pigTypes, loading, total } = useCompletedPigs(filters);
  const match = useRouteMatch();

  const csvLink = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  useEffect(() => {
    if (
      exporting &&
      pigRuns &&
      activeOrg &&
      !loading &&
      !downloading &&
      pigRuns.pigs.length === total
    ) {
      setDownloading(true);
    }
  }, [exporting, pigRuns, activeOrg, loading, total, downloading]);

  useEffect(() => {
    if (csvLink && csvLink.current && downloading) {
      csvLink.current.link.click();
      setExporting(false);
      setDownloading(false);
    }
  }, [downloading]);

  const runs: Array<gqlType.PigRun> = pigRuns ? pigRuns.pigs : [];

  // Group raw pig types by their display label so that types formatting to the
  // same label collapse into a single option. The option value is a
  // comma-joined list of every raw pig type sharing that label; selecting it
  // filters on all of them (see expandedPigTypes above).
  const currentPigTypeOptions: Array<OptionType> = useMemo(() => {
    const valuesByLabel = new Map<string, Array<string>>();

    pigTypes.forEach((type: string) => {
      const label = formatPigTypeForDisplay(type);
      const existing = valuesByLabel.get(label);

      if (existing) {
        existing.push(type);
      } else {
        valuesByLabel.set(label, [type]);
      }
    });

    return Array.from(valuesByLabel.entries()).map(([label, values]) => ({
      label, // what gets displayed
      // all raw pig types that share this label, joined into a single option
      // value that round-trips through the comma-separated URL filter param.
      value: values.join(PIG_TYPE_VALUE_SEPARATOR)
    }));
  }, [pigTypes]);

  if (!activeOrg || !pigRuns) {
    return <LoadingWrapper loading />;
  }

  // const runs: Array<gqlType.PigRun> = pigRuns ? pigRuns.pigs : [];

  const mappedPigs = formatCompletedRuns(runs, {
    timezoneAbbr: activeOrg.timezoneAbbr,
    utcOffset: activeOrg.utcOffset
  });

  const csvData = mappedPigs.map(pig => [...pig.data, pig.auditLogs]);

  const noMatch = pigRuns && runs.length === 0;

  const handleRowClick = (url: string) => {
    history.push(url);
  };

  const getCSVData = () => {
    if (!exporting) {
      setExporting(true);
      return false;
    } else {
      return downloading;
    }
  };

  if (onActivated) onActivated({ title: "completed" });

  if (empty) {
    return (
      <EmptyListState
        loading={loading}
        text="No Completed Runs to Review"
        description={`Completed runs will show up once a pig has been\nthrough the tracked segment of the pipeline.`}
      />
    );
  }

  return (
    <>
      <DesktopFilters
        totalEvents={total}
        eventsCount={runs.length}
        currentPigTypes={currentPigTypeOptions}
      />
      <MobileFilters
        eventsCount={runs.length}
        totalEvents={total}
        currentPigTypes={currentPigTypeOptions}
      />
      {noMatch ? (
        <div className={styles.NoMatchWrapper}>
          <EmptyListState
            loading={loading}
            text="No Matches"
            description={`We couldn't find any completed pig runs that\nmatch your selected filters.`}
            cta={<Primary onClick={resetAllFilters}>Clear Filters</Primary>}
          />
        </div>
      ) : (
        <div className={styles.TableWrapperDesktop}>
          <Table caption="Completed Pig Runs">
            <Row>
              {[...TABLE_HEADERS, ""].map(header => (
                <Head key={header}>{header}</Head>
              ))}
            </Row>
            <>
              {mappedPigs.map(pig => (
                <Row
                  key={`row_${pig.id}`}
                  onClick={() => handleRowClick(`${match.url}/${pig.id}`)}
                >
                  <>
                    {pig.data.map((data, i) => (
                      <Cell key={`${TABLE_HEADERS[i]}_${pig.id}`}>{data}</Cell>
                    ))}
                  </>
                  <Cell>
                    <NavLink
                      to={`${match.url}/${pig.id}`}
                      className={styles.LinkWrapper}
                    >
                      <TextLink
                        component="span"
                        icon={<ChevronRight />}
                        linkStyle="secondary"
                      >
                        {`${pig.numAuditLogs} Notes`}
                      </TextLink>
                    </NavLink>
                  </Cell>
                </Row>
              ))}
            </>
          </Table>
        </div>
      )}
      <div className={styles.TableWrapperMobile}>
        {runs.map(run => (
          <Completed pigRun={run} key={run.id} url={`${match.url}/${run.id}`} />
        ))}
      </div>
      {!noMatch && (
        <div>
          <PaginationButtons
            updatePage={page => setQueryParams({ page }, "push")}
            currentPage={page || 1}
            totalEvents={total}
            limit={pigsLimit}
          />
          <Primary
            component="div"
            icon={<Download />}
            className={styles.DownloadLink}
          >
            <CSVLink
              data={csvData}
              headers={[...TABLE_HEADERS, "notes"]}
              filename="filtered-completed-pig-runs.csv"
              onClick={getCSVData}
              ref={csvLink}
            >
              Export Filtered Data
            </CSVLink>
          </Primary>
        </div>
      )}
    </>
  );
};

export default CompletedRuns;
